import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { cn } from '@theprintbar/shared';
import { useField } from 'remix-validated-form';
import { FieldProps, useOutsideForm } from '../index';

export interface InputProps extends FieldProps<HTMLInputElement> {
	inputClassName?: string;
	labelClassName?: string;
	trailingClass?: string;
	showErrorOnHidden?: boolean;
	compact?: boolean;
}

export function Input({
	children,
	leadingIcon: LIcon,
	trailingIcon: TIcon,
	outsideForm,
	compact,
	addOn,
	label,
	showErrorOnHidden,
	type = 'text',
	name,
	placeholder,
	className,
	inputClassName,
	labelClassName,
	trailingClass,
	fieldRef,
	optional,
	...props
}: InputProps) {
	const { error, getInputProps } = outsideForm ? useOutsideForm(name) : useField(name);
	const hidden = type === 'hidden';

	return hidden ? (
		<>
			<input
				ref={fieldRef}
				{...getInputProps({
					...props,
					type,
					id: name,
				})}
			/>
			{showErrorOnHidden && error && (
				<span id={`${name}-error`} className="text-xs text-red-600">
					{error}
				</span>
			)}
		</>
	) : (
		<div className={cn('relative mb-4', !compact && 'pt-4', className)}>
			<div className="relative rounded-md">
				{!!LIcon && (
					<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<LIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
					</div>
				)}
				<div className="flex">
					{!!addOn && (
						<span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
							{addOn}
						</span>
					)}
					<div className="relative w-full">
						<input
							placeholder={placeholder || label}
							aria-invalid={!!error}
							aria-describedby={`${name}-error`}
							ref={fieldRef}
							{...getInputProps({
								...props,
								type,
								id: name,
							})}
							className={cn(
								'bg-transparent rounded-md text-sm block w-full border-blue focus:ring-blue text-blue',
								compact && 'peer placeholder-transparent pt-4 pb-0.5 bg-white',
								error && 'border-red-600',
								!!addOn ? 'rounded-none rounded-r-md' : 'rounded-md',
								!!LIcon && 'pl-10',
								(!!TIcon || !!error) && 'pr-10',
								props.disabled && 'cursor-not-allowed bg-gray-100 border-grey',
								inputClassName,
							)}
						/>
						{label && (
							<label
								htmlFor={name}
								className={cn(
									'absolute flex w-full items-center justify-between text-blue pointer-events-none',
									!!LIcon && 'ml-6 peer-focus:ml-0',
									compact
										? [
												'left-3 top-1 text-xs block text-grey transition-all ',
												'peer-[:not(:focus):placeholder-shown]:text-gray-400',
												'peer-[:not(:focus):placeholder-shown]:top-3 peer-[:not(:focus):placeholder-shown]:left-3.5 peer-[:not(:focus):placeholder-shown]:text-sm',
												'peer-focus:-translate-y-1/4',
										  ]
										: 'left-0.5 -top-5 text-sm',
									props.disabled && 'text-grey/80',
									labelClassName,
								)}
							>
								{label}
								{optional && <span className="text-xs mx-2 text-grey/80">(optional)</span>}
							</label>
						)}
						{!!TIcon && !error && (
							<div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
								<TIcon className={cn('h-5 w-5 text-gray-400', trailingClass)} aria-hidden="true" />
							</div>
						)}
						{!!error && (
							<div className="absolute inset-y-0 right-0 top-0.5 pr-3 flex items-center pointer-events-none">
								<ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
							</div>
						)}
						{error && (
							<span id={`${name}-error`} className="absolute left-0.5 -bottom-4 text-xs text-red-600">
								{error}
							</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
